import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import type { OptionItem } from "../ViewDocs/Documents.types";

export interface DocumentDropdownProps {
  id: string;
  placeholder?: string;
  multi?: boolean;
  options: OptionItem[];
  value: any;
  disabled?: boolean;
  onChange: (value: any) => void;
}

const DocumentDropdown: React.FC<DocumentDropdownProps> = ({
  id,
  placeholder,
  multi,
  options,
  value,
  disabled,
  onChange,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const isMulti = !!multi;

  const currentMultiValues: string[] = (() => {
    if (!isMulti) return [];
    if (value && typeof value === "object" && "results" in value) {
      return (value as { results: any[] }).results.map((v) => String(v));
    }
    if (Array.isArray(value)) {
      return value.map((v) => String(v));
    }
    if (typeof value === "string" && value.length > 0) {
      return [value];
    }
    return [];
  })();

  const currentKey: string = !isMulti
    ? value == null
      ? ""
      : String(value)
    : "";

  const selectedOptions = isMulti
    ? options.filter((o) => currentMultiValues.includes(String(o.key)))
    : options.filter((o) => String(o.key) === currentKey);

  const summaryLabel = (() => {
    if (selectedOptions.length === 0) {
      return placeholder || (isMulti ? "Select options" : "Select an option");
    }
    return selectedOptions.map((o) => o.text).join(", ");
  })();

  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const container = containerRef.current;
      const menu = menuRef.current;

      if (container && container.contains(target)) {
        return;
      }
      if (menu && menu.contains(target)) {
        return;
      }

      setOpen(false);
    };

    const handleScroll = () => {
      // Recompute position on scroll so menu tracks trigger
      updatePosition();
    };

    updatePosition();

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  const handleOptionClick = (opt: OptionItem) => {
    if (disabled) return;

    if (isMulti) {
      const key = String(opt.key);
      const set = new Set(currentMultiValues);
      if (set.has(key)) {
        set.delete(key);
      } else {
        set.add(key);
      }
      onChange({ results: Array.from(set) });
    } else {
      onChange(opt.key);
      setOpen(false);
    }
  };

  const menu =
    open && !disabled && menuPosition
      ? ReactDOM.createPortal(
          <div
            className="list-dropdown-multi-menu"
            role="listbox"
            aria-multiselectable={isMulti || undefined}
            style={{
              position: "fixed",
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
              zIndex: 2000,
            }}
            ref={menuRef}
          >
            {options.map((opt) => {
              const key = String(opt.key);
              const isSelected = isMulti
                ? currentMultiValues.includes(key)
                : currentKey === key;

              return (
                <button
                  key={key}
                  type="button"
                  className={
                    "list-dropdown-multi-option" +
                    (isSelected ? " list-dropdown-multi-option--selected" : "")
                  }
                  onClick={() => handleOptionClick(opt)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span className="list-dropdown-multi-option-check">
                    {isSelected ? "✓" : ""}
                  </span>
                  <span className="list-dropdown-multi-option-text">
                    {opt.text}
                  </span>
                </button>
              );
            })}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="list-dropdown-wrapper" ref={containerRef}>
      <button
        type="button"
        id={id}
        className="form-select list-dropdown-multi-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
      >
        <span className="list-dropdown-multi-label">{summaryLabel}</span>
        <span className="list-dropdown-multi-caret" aria-hidden="true" />
      </button>
      {menu}
    </div>
  );
};

export default DocumentDropdown;
